import { useCallback, useEffect, useRef, useState } from 'react';

interface ICodeFrameUrlProps {
  url: string;
  style?: React.CSSProperties;
  skeleton?: React.ReactNode;
  onReady?: () => void;
}

const IFRAME_SANDBOX = 'allow-forms allow-modals allow-pointer-lock allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation allow-downloads allow-presentation';
const IFRAME_ALLOW = 'accelerometer; camera; encrypted-media; display-capture; geolocation; gyroscope; microphone; midi; clipboard-read; clipboard-write; web-share';

const CodeFrame = ({ url, style, skeleton, onReady }: ICodeFrameUrlProps) => {
  const activeRef = useRef<HTMLIFrameElement>(null);
  const bufferRef = useRef<HTMLIFrameElement>(null);
  const prevUrlRef = useRef<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeUrl, setActiveUrl] = useState('');
  const [bufferUrl, setBufferUrl] = useState('');
  const [showBuffer, setShowBuffer] = useState(false);
  // Track whether the first load has fired onReady — prevents duplicate signals
  const initialReadyFiredRef = useRef(false);

  const handleBufferLoad = useCallback(() => {
    // Buffer iframe finished loading — swap it to front
    setShowBuffer(true);

    // After the transition, update active with the new URL and hide buffer.
    // Fire onReady AFTER the swap completes (not when buffer loads) so the caller
    // can be sure the visible iframe shows the new content — prevents overlay fade
    // from starting while the active iframe still shows old content.
    const timer = setTimeout(() => {
      setActiveUrl(bufferUrl);
      setShowBuffer(false);
      setBufferUrl('');

      // Revoke old blob URL if it was a blob
      if (prevUrlRef.current && prevUrlRef.current.startsWith('blob:')) {
        try { URL.revokeObjectURL(prevUrlRef.current); } catch {}
      }
      prevUrlRef.current = bufferUrl;

      // Signal ready AFTER active iframe has been updated with new content
      onReady?.();
    }, 120);

    return () => clearTimeout(timer);
  }, [bufferUrl, onReady]);

  useEffect(() => {
    if (!url) return;

    if (isInitialLoad || !activeUrl) {
      // First load — set directly
      setActiveUrl(url);
      setIsInitialLoad(false);
      prevUrlRef.current = url;
    } else if (url !== activeUrl && url !== bufferUrl) {
      // Subsequent updates — load in buffer iframe
      setBufferUrl(url);
      setShowBuffer(false);
    }
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!url) {
    return null;
  }

  const iframeStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    border: 'none',
    ...style,
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {isInitialLoad && skeleton && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
          {skeleton}
        </div>
      )}
      {/* Active iframe — always visible */}
      {activeUrl && (
        <iframe
          ref={activeRef}
          className="bds-code-frame"
          src={activeUrl}
          sandbox={IFRAME_SANDBOX}
          allow={IFRAME_ALLOW}
          allowFullScreen
          onLoad={() => {
            setIsInitialLoad(false);
            // Fire onReady for the very first load only — subsequent updates go through
            // the buffer path which fires onReady after the swap completes.
            if (!initialReadyFiredRef.current) {
              initialReadyFiredRef.current = true;
              onReady?.();
            }
          }}
          style={{
            ...iframeStyle,
            zIndex: 1,
            opacity: showBuffer ? 0 : 1,
            transition: 'opacity 100ms ease',
          }}
        />
      )}
      {/* Buffer iframe — loads new URL behind the scenes */}
      {bufferUrl && (
        <iframe
          ref={bufferRef}
          className="bds-code-frame"
          src={bufferUrl}
          sandbox={IFRAME_SANDBOX}
          allow={IFRAME_ALLOW}
          allowFullScreen
          onLoad={handleBufferLoad}
          style={{
            ...iframeStyle,
            zIndex: showBuffer ? 2 : 0,
            opacity: showBuffer ? 1 : 0,
            transition: 'opacity 100ms ease',
          }}
        />
      )}
    </div>
  );
};

export default CodeFrame;
